import { useState } from "react";
import { RiCheckboxCircleLine } from "react-icons/ri";
import symbolWatermark from "../assets/loanInMinutes/water_mark_2.png";


import Navbar from "./Navbar";
import calculatorImage from "../assets/loanInMinutes/Rectangle.png";
import { APPLY_NOW_PATH } from "../utils/appLinks";

const charges = [
  "Loan Amount: ₹5,000 to ₹1,00,000",
  "Tenure: 10 to 60 Days",
  "No Pre-closure Charges",
  "No Prepayment Charges",
  "APR calculated by formula",
  "Net Monthly Salary ≥ ₹35,000",
];

export const LoanCalculatorSection = () => {
  const [amount, setAmount] = useState(15000);
  const [tenure, setTenure] = useState(30);
  const [rate, setRate] = useState(1);

  const interest = (amount * (rate / 100) * tenure);
  const totalPayable = amount + interest;
  const apr = (rate * 365).toFixed(1);

  return (
    <section className="py-20 sm:pt-0 sm:pb-20 bg-white relative overflow-hidden">

       {/* Symbol watermark — top right */}
      <img
        src={symbolWatermark}
        alt=""
        className="absolute top-0 -left-8 sm:-left-10 w-45 h-45 sm:w-64 sm:h-64 object-contain opacity-100 pointer-events-none select-none"
      />
     

      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-12">
          <span className="text-[#CC1A1A] text-xs font-black tracking-[0.3em] uppercase mb-3">
            LOAN CALCULATOR
          </span>
          <div className="w-8 h-0.5 bg-[#CC1A1A] mb-5" />
          <h2 className="text-4xl xl:text-5xl font-bold text-[#1a1a4e] mb-4">
            Look Before You Leap
          </h2>
          <p className="text-gray-400 font-light text-base">
            Understand your repayment journey with our smart loan calculator. No surprises, ever.
          </p>
        </div>

        {/* Main container */}
        <div className="flex flex-col lg:flex-row gap-5 overflow-hidden">

          {/* Desktop image only */}
          <div className="hidden lg:block lg:w-[33%] flex-shrink-0">
            <img
              src={calculatorImage}
              alt="Loan calculator"
              className="w-full h-full object-fit"
              style={{ minHeight: "460px" }}
            />
          </div>

          {/* Content div — wraps Div 1 and Div 2 */}
          <div className="relative flex flex-col lg:flex-row flex-1 rounded-lg border border-[#ef4949] overflow-hidden">

          {/* Div 1 — Sliders */}
          <div className="w-full lg:w-[50%] flex flex-col justify-center gap-8 px-8 py-10 bg-white lg:border-r lg:border-[#ef4949]">

            <div className="flex flex-col gap-3">
              <label className="text-gray-500 text-sm font-medium">Estimated Amount Required</label>
              <div className="border border-gray-200 rounded px-4 py-3 text-gray-800 font-semibold text-base">
                ₹ {amount.toLocaleString("en-IN")}
              </div>
              <input type="range" min={5000} max={100000} step={1000} value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full accent-[#CC1A1A] cursor-pointer" />
              <div className="flex justify-between text-xs text-gray-400 font-medium">
                <span>₹10K</span><span>₹1L</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-gray-500 text-sm font-medium">Your Preferred Loan Term ( In Days )</label>
              <div className="border border-gray-200 rounded px-4 py-3 text-gray-800 font-semibold text-base">
                {tenure}
              </div>
              <input type="range" min={10} max={60} step={1} value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
                className="w-full accent-[#CC1A1A] cursor-pointer" />
              <div className="flex justify-between text-xs text-gray-400 font-medium">
                <span>10 Days</span><span>60 Days</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-gray-500 text-sm font-medium">Interest Rate</label>
              <div className="border border-gray-200 rounded px-4 py-3 text-gray-800 font-semibold text-base">
                {rate.toFixed(1)}%
              </div>
              <input type="range" min={0.5} max={1.5} step={0.1} value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full accent-[#CC1A1A] cursor-pointer" />
              <div className="flex justify-between text-xs text-gray-400 font-medium">
                <span>0.5%</span><span>1.5%</span>
              </div>
            </div>
          </div>

          {/* Div 2 — Summary + Rate & Charges (two sub-divs stacked) */}
          <div className="w-full lg:w-[50%] flex flex-col">

            {/* Sub-div 1 — Summary rows + button (red on mobile, white on desktop) */}
            <div className="flex flex-col px-8 py-5 bg-[#e6291f] lg:bg-white">
              {[
                { label: "Loan Amount", value: `₹ ${amount.toLocaleString("en-IN")}` },
                { label: "Tenure", value: `${tenure} Days` },
                { label: "Interest Rate", value: `${rate.toFixed(1)}%` },
                { label: "APR", value: `${apr}%` },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2">
                  <span className="text-white/70 lg:text-gray-500 text-lg sm:text-base">{row.label}</span>
                  <span className="font-semibold text-white lg:text-gray-800 text-lg sm:text-base">{row.value}</span>
                </div>
              ))}

              <div className="flex justify-between items-center pt-4 pb-5 border-t border-gray-200">
                <span className="text-white lg:text-gray-600 font-medium text-xl sm:text-lg">Total Payable</span>
                <span className="text-white lg:text-[#CC1A1A] font-medium text-2xl sm:text-lg">
                  ₹ {totalPayable.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <a
                href={APPLY_NOW_PATH}
                className="block w-full py-3 bg-white lg:bg-[#e6291f] text-[#CC1A1A] lg:text-white font-bold text-center text-base rounded-xl hover:opacity-90 transition-opacity"
              >
                Get Started
              </a>
            </div>

            {/* Sub-div 2 — Rate & Charges (always white) */}
            <div className="flex flex-col px-8 py-5 bg-[#e6291f] sm:bg-white flex-1">
              <p className="text-white sm:text-red-500 text-lg mb-3">Rate &amp; Charges</p>
              <ul className="flex flex-col gap-2">
                {charges.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-white sm:text-gray-500 text-base font-light">
                    <RiCheckboxCircleLine className="text-white sm:text-red-500 mt-0.5 flex-shrink-0 text-lg" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

          </div>

          </div>{/* end content div */}

        </div>
      </div>
    </section>
  );
};

const LoanCalculator = () => {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="pt-[73px]">
        <LoanCalculatorSection />
      </div>
    </div>
  );
};

export default LoanCalculator;
